import { useState, useEffect } from "react";
import { useAuth, API_URL } from "../../context/AuthContext";
import {
  Box,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface CategoriesSectionProps {
  categories: Array<{ id: number; name: string; name_en?: string; sort_order: number }>;
  onChange: () => void;
}

export default function CategoriesSection({ categories, onChange }: CategoriesSectionProps) {
  const [localCategories, setLocalCategories] = useState(categories);
  const [newName, setNewName] = useState("");
  const [newNameEn, setNewNameEn] = useState("");
  const [newSortOrder, setNewSortOrder] = useState("0");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editNameEn, setEditNameEn] = useState("");
  const [editSortOrder, setEditSortOrder] = useState("0");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const { token } = useAuth();

  useEffect(() => setLocalCategories(categories), [categories]);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/categories`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: newName, name_en: newNameEn, sort_order: parseInt(newSortOrder) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setNewName("");
      setNewNameEn("");
      setNewSortOrder("0");
      setMsg("Dodano kategorię");
      onChange();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSave = async (id: number) => {
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/categories/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name: editName, name_en: editNameEn, sort_order: parseInt(editSortOrder) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditingId(null);
      setMsg("Zapisano");
      onChange();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Usunąć tę kategorię?")) return;
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/categories/${id}`, { method: "DELETE", headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMsg("Usunięto");
      onChange();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleMove = async (id: number, direction: number) => {
    const cat = localCategories.find((c) => c.id === id);
    if (!cat) return;
    const newOrder = cat.sort_order + direction;
    if (newOrder < 0) return;
    try {
      await fetch(`${API_URL}/api/categories/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ sort_order: newOrder }),
      });
      onChange();
    } catch (e: any) {
      setError("Błąd zmiany kolejności");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#aaa" }}>Kolejność</TableCell>
              <TableCell sx={{ color: "#aaa" }}>Nazwa (PL)</TableCell>
              <TableCell sx={{ color: "#aaa" }}>Nazwa (EN)</TableCell>
              <TableCell sx={{ color: "#aaa" }} align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {localCategories.map((cat) => (
              <TableRow key={cat.id}>
                {editingId === cat.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" type="number" value={editSortOrder} onChange={(e) => setEditSortOrder(e.target.value)} sx={{ input: { color: "#f0f0f0" }, width: 60 }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editName} onChange={(e) => setEditName(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editNameEn} onChange={(e) => setEditNameEn(e.target.value)} placeholder="EN" sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleSave(cat.id)} sx={{ color: "#4caf50" }}>Zapisz</Button>
                      <Button size="small" onClick={() => setEditingId(null)} sx={{ color: "#aaa" }}>Anuluj</Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ color: "#f0f0f0" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleMove(cat.id, -1)} disabled={cat.sort_order <= 0} sx={{ color: "#aaa", p: 0.25 }}>↑</IconButton>
                        <IconButton size="small" onClick={() => handleMove(cat.id, 1)} sx={{ color: "#aaa", p: 0.25 }}>↓</IconButton>
                        <Typography variant="body2" sx={{ color: "#888", ml: 0.5 }}>{cat.sort_order}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#f0f0f0" }}>{cat.name}</TableCell>
                    <TableCell sx={{ color: "#aaa", fontSize: "0.85rem" }}>{cat.name_en || "—"}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => { setEditingId(cat.id); setEditName(cat.name); setEditNameEn(cat.name_en || ""); setEditSortOrder(String(cat.sort_order)); }} sx={{ color: "#1976d2" }}>Edytuj</Button>
                      <IconButton size="small" onClick={() => handleDelete(cat.id)} sx={{ color: "#f44336" }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {localCategories.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: "#888", textAlign: "center" }}>Brak kategorii</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
        <TextField size="small" type="number" placeholder="Kolejność" value={newSortOrder} onChange={(e) => setNewSortOrder(e.target.value)} sx={{ flex: 0.5, input: { color: "#f0f0f0" } }} />
        <TextField size="small" placeholder="Nazwa PL" value={newName} onChange={(e) => setNewName(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <TextField size="small" placeholder="Nazwa EN" value={newNameEn} onChange={(e) => setNewNameEn(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ color: "#4caf50" }}>Dodaj</Button>
      </Box>
    </Box>
  );
}
